import { faUtensils, faCalendarDays, faCartPlus } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Stack, Typography, IconButton, Tooltip, Box } from '@vapor/material'
import { HicIconButton } from 'src/app/components/hic-vapor/button/icon-button'
import { HiCIcon } from 'src/app/components/icons/icon'
import ReservationPriceField from '../reservation-price-field'
import { DailyPricesRow, ResourceRateInfo, SelectedReservationChoice } from '../../form/reservation-new.type'
import { useTranslation } from 'react-i18next'
import { faCartShoppingFast } from '@fortawesome/pro-solid-svg-icons'

export type ChargeCategorySectionProps = {
  resourceRate: ResourceRateInfo
  index: number
  initialTotalForRoom: number[] | undefined
  reservationChoiceState: SelectedReservationChoice[]
  stopSell: boolean
  reservationChoiceHasError: boolean
  setReservationChoiceState: (value: SelectedReservationChoice[]) => void
  setOpenErrorsDialog: (callback: () => void) => void
  addChoiceToCart: (resourceRate: ResourceRateInfo, choiceId: number) => void
  fastAddChoiceToCart: (resourceRate: ResourceRateInfo, choiceId: number) => void
  showDailyPrices: (event: React.MouseEvent<HTMLButtonElement>) => void
  setDailyPricesRows: (row: DailyPricesRow[] | undefined) => void
}

export default function ChargeCategorySection(props: ChargeCategorySectionProps) {
  const {
    resourceRate,
    index,
    initialTotalForRoom,
    reservationChoiceState,
    stopSell,
    reservationChoiceHasError,
    setReservationChoiceState,
    setOpenErrorsDialog,
    addChoiceToCart,
    fastAddChoiceToCart,
    showDailyPrices,
    setDailyPricesRows,
  } = props

  const { t } = useTranslation()

  return (
    <Stack
      direction="row"
      justifyContent="space-between"
      sx={{
        display: 'flex',
        py: 0.5,
        alignContent: 'space-between',
        alignItems: 'flex-end',
        p: 1,
        pointerEvents: 'auto',
        border: '1px solid transparent',
        transition: 'ease-in 0.2s',
        '&:hover': { border: '1px solid var(--RichElectricBlue)' },
      }}
      spacing={{ xs: 1 }}
    >
      <Stack direction="row" m={0.5} spacing={1} justifyContent={'space-between'} alignContent={'space-between'} alignItems="center" width="100%" height="100%">
        <Stack direction="row" justifyContent="center" alignItems="center">
          <HiCIcon mr={1}>
            <FontAwesomeIcon icon={faUtensils} style={{ width: '12px', height: '14px' }} />
          </HiCIcon>
          <Typography variant="body">{resourceRate.chargeCategoryName}</Typography>
        </Stack>
      </Stack>
      <Stack direction="row" justifyContent="end" spacing={1} alignItems="flex-end" width="100%">
        <ReservationPriceField
          initialTotalPerRoom={initialTotalForRoom ? initialTotalForRoom[index] : 0}
          onChange={(value) => {
            const choiceId = reservationChoiceState.findIndex((resChoice) => resChoice.resourceTypeId === resourceRate.resourceTypeId && resChoice.chargeCategoryId === resourceRate.chargeCategoryId)
            if (choiceId >= 0) {
              const tmpReservation = [...reservationChoiceState]
              tmpReservation[choiceId] = {
                ...tmpReservation[choiceId],
                totalPrice: value,
              }
              setReservationChoiceState([...tmpReservation])
            }
          }}
        />
        <Stack direction="row" justifyContent="center" alignItems="center">
          <Tooltip title={t('pms.booking.reservation_new.calendar.tooltip')} enterDelay={1000} enterNextDelay={500}>
            <div>
              <IconButton
                color={stopSell ? 'error' : 'primary'}
                size="small"
                onClick={(event) => {
                  const choiceId = reservationChoiceState.findIndex(
                    (resChoice) => resChoice.resourceTypeId === resourceRate.resourceTypeId && resChoice.chargeCategoryId === resourceRate.chargeCategoryId,
                  )
                  if (choiceId >= 0) {
                    showDailyPrices(event)
                    const discountDiff = reservationChoiceState[choiceId].totalPrice - reservationChoiceState[choiceId].initialTotalPrice
                    setDailyPricesRows(
                      resourceRate.dailyPrice.map((dailyPrice) => {
                        return {
                          dailyPrice: dailyPrice,
                          discount: discountDiff !== 0 ? dailyPrice.price + discountDiff : undefined,
                          stopSell: dailyPrice.restriction === 'STOPSELL',
                        }
                      }),
                    )
                  }
                }}
                style={{
                  pointerEvents: 'auto',
                }}
              >
                <FontAwesomeIcon icon={faCalendarDays} />
              </IconButton>
            </div>
          </Tooltip>
          <Tooltip title={t('pms.booking.reservation_new.add.tooltip')} enterDelay={1000} enterNextDelay={500}>
            <div>
              <IconButton
                size="small"
                style={{
                  pointerEvents: 'auto',
                }}
                color={'info'}
                onClick={() => {
                  const choiceId = reservationChoiceState.findIndex(
                    (resChoice) => resChoice.resourceTypeId === resourceRate.resourceTypeId && resChoice.chargeCategoryId === resourceRate.chargeCategoryId,
                  )
                  if (choiceId >= 0) {
                    if (reservationChoiceHasError) {
                      setOpenErrorsDialog(() => addChoiceToCart(resourceRate, choiceId))
                    } else {
                      addChoiceToCart(resourceRate, choiceId)
                    }
                  }
                }}
              >
                <FontAwesomeIcon icon={faCartPlus} />
              </IconButton>
            </div>
          </Tooltip>
        </Stack>
      </Stack>
      <Box>
        <Stack direction="row" alignItems={'center'}>
          <div>
            <HicIconButton
              size="small"
              variant={reservationChoiceHasError ? 'outlined' : 'contained'}
              startIcon={<FontAwesomeIcon icon={faCartShoppingFast} />}
              style={{
                pointerEvents: 'auto',
                marginRight: 1,
              }}
              onClick={() => {
                const choiceId = reservationChoiceState.findIndex(
                  (resChoice) => resChoice.resourceTypeId === resourceRate.resourceTypeId && resChoice.chargeCategoryId === resourceRate.chargeCategoryId,
                )
                if (choiceId >= 0) {
                  if (reservationChoiceHasError) {
                    setOpenErrorsDialog(() => fastAddChoiceToCart(resourceRate, choiceId))
                  } else {
                    fastAddChoiceToCart(resourceRate, choiceId)
                  }
                }
              }}
            >
              {t('pms.booking.reservation_new.add.button')}
            </HicIconButton>
          </div>
        </Stack>
      </Box>
    </Stack>
  )
}
